// React
import { useState, useEffect, useCallback } from 'react';

// Store
import useSiteStore from '../../store/siteStore.js';
import useProfileStore from '../../store/profileStore.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Components
import { 
    CloseOutlined, 
    DeleteOutlined, 
    PublicOutlined, 
    PersonOutlined,
    FitnessCenterOutlined,
    DescriptionOutlined 
} from '@mui/icons-material';
import { 
    Box, 
    Card,
    IconButton, 
    Modal, 
    Paper, 
    Typography,
    Tooltip
} from '@mui/material';

// Style
import { blue, grey, red, orange } from '@mui/material/colors/index.js';
import { defaultColorScheme, tealColorScheme, darkTheme } from '../../styles/colorSchemes/index.js';

// Other 
import { deleteTextblock, getTextblocksOfCoach } from '../../service/httpService/textblocks/index.js';

const convertFokusIdToString = (trainingsfokus) => {
    switch (trainingsfokus) {
        case 1: return 'Kraft';
        case 2: return 'Ausdauer';
        case 3: return 'Technik';
        case 4: return 'Taktik';
        case 5: return 'Koordination';
        case 6: return 'Schnelligkeit';
        case 7: return 'Wettkampf/Liga';
        case 8: return 'Ruhetag';
        case 9: return 'Reisetag';
        default: return 'Unbekannt';
    }
};

const TextblockView = (props) => {
    const { handleSelectTextblock, onClose, open, setTextblockId, setTextblocks, textblockId, textblocks, trainingsSessionIndex } = props;
    const { setFailMessage, setSnackbarOpenFail } = useSiteStore();
    const { colorScheme, profileType, userEmail } = useProfileStore();
    const { isDarkMode } = useAppTheme();

    const [activeTab, setActiveTab] = useState(0);
    const primaryColor = isDarkMode ? orange[500] : blue[600];

    // Use the same color scheme as WeeklyScheduleTable
    let colors = defaultColorScheme.trainingFocusColor;
    if (colorScheme === 'teal') {
        colors = tealColorScheme.trainingFocusColor;
    }

    const loadTextblocks = useCallback(() => {
        getTextblocksOfCoach(userEmail).then(response => {
            setTextblocks(response.data);
        }).catch();
    }, [userEmail, setTextblocks]);

    useEffect(() => {
        if (open) {
            loadTextblocks();
        }
    }, [open, loadTextblocks]);

    const deleteSelectedTextblock = async (id) => {
        try {
            await deleteTextblock(id);
            if (textblockId === id) {
                setTextblockId(trainingsSessionIndex, undefined);
            }
        } catch (err) {
            setFailMessage(`Textbaustein konnte nicht gelöscht werden!`);
            setSnackbarOpenFail(true);
            return;
        }
        loadTextblocks();
    };

    // Filter textblocks based on active tab
    const safeTextblocks = textblocks || [];
    const filteredTextblocks = safeTextblocks.filter(textblock => 
        activeTab === 0 ? textblock.isglobal === true : textblock.isglobal === false
    ).sort((a, b) => a.trainingfocus - b.trainingfocus || a.textblockid - b.textblockid);

    const globalCount = safeTextblocks.filter(tb => tb.isglobal === true).length;
    const privateCount = safeTextblocks.filter(tb => tb.isglobal === false).length;

    // Check if user can delete (private always deletable, global only by nationalCoach)
    const canDelete = (textblock) => {
        return !textblock.isglobal || profileType === 'nationalCoach';
    };

    return (
        <Modal 
            open={open} 
            onClose={onClose}
            sx={{ 
                display: 'flex', 
                pb: 2, 
                pt: 2, 
                alignItems: 'center', 
                justifyContent: 'center', 
                overflow: 'auto',
                '@media (max-width: 900px)': {
                    alignItems: 'flex-start',
                    p: 0,
                    overflow: 'auto'
                }
            }}
        >
            <Paper 
                elevation={8}
                sx={{ 
                    width: { xs: '95%', sm: '90%', md: '80%' },
                    maxWidth: '700px',
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    border: 'none',
                    maxHeight: '85vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: isDarkMode ? darkTheme.accordionBg : '#fafafa',
                    position: 'relative',
                    boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : undefined,
                    '@media (max-width: 900px)': {
                        width: '100%',
                        minHeight: '100vh',
                        maxHeight: '100vh',
                        borderRadius: 0,
                        m: 0
                    }
                }}
            >
                {/* Close Button */}
                <IconButton 
                    onClick={onClose}
                    sx={{ 
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 10,
                        bgcolor: isDarkMode ? darkTheme.headerBg : 'rgba(255, 255, 255, 0.9)',
                        color: isDarkMode ? darkTheme.textSecondary : undefined,
                        '&:hover': {
                            bgcolor: isDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.05)',
                            transform: 'rotate(90deg)',
                            transition: 'transform 0.3s ease'
                        }
                    }}
                >
                    <CloseOutlined />
                </IconButton>

                {/* Header */}
                <Box sx={{ mb: 2, pr: 4 }}>
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            fontWeight: 600, 
                            color: isDarkMode ? darkTheme.textPrimary : grey[800],
                        }}
                    >
                        Textbausteine
                    </Typography>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: isDarkMode ? darkTheme.textSecondary : grey[500],
                            mt: 0.5
                        }}
                    >
                        Wählen Sie einen Textbaustein aus
                    </Typography>
                </Box>

                {/* Tabs - Sliding Switch */}
                <Box sx={{ mb: 3 }}>
                    <Box 
                        sx={{ 
                            position: 'relative',
                            bgcolor: isDarkMode ? darkTheme.surface : grey[200],
                            borderRadius: '12px',
                            p: 0.5,
                            display: 'flex',
                        }}
                    >
                        {/* Sliding Background */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 4,
                                left: activeTab === 0 ? 4 : 'calc(50% + 2px)',
                                width: 'calc(50% - 6px)',
                                height: 'calc(100% - 8px)',
                                bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                                borderRadius: '10px',
                                boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.12)',
                                transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                zIndex: 0,
                            }}
                        />
                        
                        {/* Global Tab */}
                        <Box
                            onClick={() => setActiveTab(0)}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                py: 1.25,
                                px: 2,
                                cursor: 'pointer',
                                position: 'relative',
                                zIndex: 1,
                                borderRadius: '10px',
                                transition: 'color 0.2s ease',
                                color: activeTab === 0 ? primaryColor : (isDarkMode ? darkTheme.textSecondary : grey[600]),
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                userSelect: 'none',
                                '&:hover': {
                                    color: activeTab === 0 ? primaryColor : (isDarkMode ? darkTheme.textPrimary : grey[800]),
                                }
                            }}
                        >
                            <PublicOutlined sx={{ fontSize: 20 }} />
                            Global ({globalCount})
                        </Box>

                        {/* Privat Tab */}
                        <Box
                            onClick={() => setActiveTab(1)}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                py: 1.25,
                                px: 2,
                                cursor: 'pointer',
                                position: 'relative',
                                zIndex: 1,
                                borderRadius: '10px',
                                transition: 'color 0.2s ease',
                                color: activeTab === 1 ? primaryColor : (isDarkMode ? darkTheme.textSecondary : grey[600]),
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                userSelect: 'none',
                                '&:hover': {
                                    color: activeTab === 1 ? primaryColor : (isDarkMode ? darkTheme.textPrimary : grey[800]),
                                }
                            }}
                        >
                            <PersonOutlined sx={{ fontSize: 20 }} />
                            Privat ({privateCount})
                        </Box>
                    </Box>
                </Box>

                {/* Textblock List */}
                <Box sx={{ 
                    flex: 1, 
                    overflow: 'auto',
                    pr: 1,
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: isDarkMode ? darkTheme.headerBg : grey[100],
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: isDarkMode ? darkTheme.cardBorder : grey[300],
                        borderRadius: '3px',
                        '&:hover': {
                            background: isDarkMode ? darkTheme.textSecondary : grey[400],
                        }
                    }
                }}>
                    {filteredTextblocks.length === 0 ? (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            py: 6,
                            color: isDarkMode ? darkTheme.textSecondary : grey[500]
                        }}>
                            <DescriptionOutlined sx={{ fontSize: 48, mb: 2, color: isDarkMode ? darkTheme.textSecondary : grey[300] }} />
                            <Typography variant="body1" fontWeight={500}>
                                Keine Textbausteine vorhanden
                            </Typography>
                            <Typography variant="body2" color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>
                                {activeTab === 0 ? 'Keine globalen Textbausteine gefunden' : 'Keine privaten Textbausteine gefunden'}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {filteredTextblocks.map((textblock, index) => (
                                <Card
                                    key={textblock.textblockid}
                                    sx={{
                                        borderRadius: '10px',
                                        boxShadow: isDarkMode ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.08)',
                                        transition: 'all 0.2s ease',
                                        borderLeft: `4px solid ${colors[textblock.trainingfocus - 1] || grey[400]}`,
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                                        '&:hover': {
                                            boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.12)',
                                            transform: 'translateX(4px)',
                                            backgroundColor: isDarkMode ? darkTheme.cardHover : grey[50]
                                        }
                                    }}
                                    onClick={() => {
                                        handleSelectTextblock(textblock, trainingsSessionIndex);
                                        onClose();
                                    }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 2,
                                        gap: 2,
                                        bgcolor: isDarkMode ? darkTheme.cardBg : 'white'
                                    }}>
                                        {/* Number Badge */}
                                        <Box sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: colors[textblock.trainingfocus - 1] || grey[400],
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '0.9rem',
                                            flexShrink: 0
                                        }}>
                                            {index + 1}
                                        </Box>

                                        {/* Content */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            {/* Training Focus */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <FitnessCenterOutlined sx={{ 
                                                    fontSize: 16, 
                                                    color: colors[textblock.trainingfocus - 1] || grey[400] 
                                                }} />
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight={600}
                                                    sx={{
                                                        color: colors[textblock.trainingfocus - 1] || grey[600],
                                                        fontSize: '0.85rem'
                                                    }}
                                                >
                                                    {convertFokusIdToString(textblock.trainingfocus)}
                                                </Typography>
                                            </Box>

                                            {/* Description */}
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: isDarkMode ? darkTheme.textPrimary : grey[700],
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    lineHeight: 1.5
                                                }}
                                            >
                                                {textblock.textblock}
                                            </Typography>
                                        </Box>

                                        {/* Delete Button */}
                                        {canDelete(textblock) && (
                                            <Tooltip title="Löschen">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteSelectedTextblock(textblock.textblockid);
                                                    }}
                                                    sx={{
                                                        color: red[500],
                                                        flexShrink: 0,
                                                        '&:hover': {
                                                            color: red[700],
                                                            backgroundColor: red[50]
                                                        }
                                                    }}
                                                >
                                                    <DeleteOutlined fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Card>
                            ))}
                        </Box>
                    )}
                </Box>
            </Paper>
        </Modal>    
    );
};

export default TextblockView;